// @flow

import type { Endpoints, EntityFieldCollection, SortParams } from '../types';
import {
    createdAtField,
    getCreatedAtSort,
    idField,
    payfactoMerchantNoField,
    paymentModeField,
} from '../abstract/StoredEntity';
import type { StoredEntity } from '../abstract/StoredEntity';
import { constructEndpoints } from '../helpers';
import PaymentMethod from './PaymentMethod';
import Bill from './Bill';

class Transaction implements StoredEntity {
    static classInternalName = 'transaction';

    static fields: EntityFieldCollection = {
        id: idField,
        createdAt: createdAtField,
        billId: {
            displayName: 'Bill',
            recordName: 'bill_id',
            dataType: {
                primitive: 'ref',
                isArray: false,
                extraData: {
                    model: () => Bill,
                    maxValues: 1,
                },
            },
            supportsQuery: true,
            supportsSort: false,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: true,
        },
        paymentMethodId: {
            displayName: 'Payment Method',
            recordName: 'payment_method_id',
            dataType: {
                primitive: 'ref',
                isArray: false,
                extraData: {
                    model: () => PaymentMethod,
                    maxValues: 1,
                },
            },
            supportsQuery: true,
            supportsSort: false,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: true,
        },
        type: {
            displayName: 'Type',
            recordName: 'tx_type',
            dataType: {
                primitive: 'enum',
                isArray: false,
                extraData: {
                    1: 'Pre-auth',
                    2: 'Capture',
                    3: 'Refund',
                },
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: true,
        },
        amount: {
            displayName: 'Amount',
            recordName: 'amount',
            dataType: {
                primitive: 'amt',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: true,
        },
        status: {
            displayName: 'Status',
            recordName: 'tx_status',
            dataType: {
                primitive: 'enum',
                isArray: false,
                extraData: {
                    1: 'Processing',
                    2: 'Reprocessing',
                    3: 'Approved',
                    4: 'Failed',
                    5: 'Declined',
                    6: 'Canceled',
                },
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: true,
        },
        payfactoTxNo: {
            displayName: 'PayFacto Transaction Number',
            recordName: 'payfacto_tx_no',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: true,
            displayOnMain: false,
        },
        payfactoAuthNo: {
            displayName: 'PayFacto Authorization Number',
            recordName: 'payfacto_auth_no',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: true,
            displayOnMain: false,
        },
        payfactoReturnCode: {
            displayName: 'PayFacto Return Code',
            recordName: 'payfacto_return_code',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: true,
            displayOnMain: false,
        },
        payfactoBilledAmount: {
            displayName: 'PayFacto Billed Amount',
            recordName: 'payfacto_billed_amount',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: true,
            displayOnMain: false,
        },
        paymentMode: paymentModeField,
        payfactoMerchantNo: payfactoMerchantNoField,
        provider: {
            displayName: 'Provider',
            recordName: 'provider',
            dataType: {
                primitive: 'enum',
                isArray: false,
                extraData: {
                    payfacto: 'PayFacto',
                    stripe: 'Stripe',
                    mock: 'Mock',
                    none: 'None',
                },
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: true,
            displayOnMain: true,
        },
        providerTxId: {
            displayName: 'Provider Tx ID',
            recordName: 'provider_tx_id',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: false,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: true,
            displayOnMain: true,
        },
        providerAuthId: {
            displayName: 'Provider Auth / Charge ID',
            recordName: 'provider_auth_id',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: false,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: true,
            displayOnMain: false,
        },
    };

    static fileFields: EntityFieldCollection = {};

    static defaultSort: SortParams = getCreatedAtSort('created_at');

    get id() {
        return this.record[Transaction.fields.id.recordName];
    }

    get displayName() {
        return this.record[Transaction.fields.id.recordName];
    }

    static endpoints: Endpoints = constructEndpoints('transactions');

    constructor(record: any) {
        this.record = record;
    }
}

export default Transaction;
